import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, ArrowRight } from "lucide-react";
import api, { fileUrl } from "@/lib/api";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [activeIdx, setActiveIdx] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/media", { params: { kind: "photo" } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, []);

  const close = () => setActiveIdx(null);
  const next = () => setActiveIdx((i) => (i + 1) % items.length);
  const prev = () => setActiveIdx((i) => (i - 1 + items.length) % items.length);

  useEffect(() => {
    const onKey = (e) => {
      if (activeIdx === null) return;
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [activeIdx, items.length]);

  return (
    <div className="bg-black min-h-screen anim-fadeup" data-testid="gallery-page">
      <section className="border-b border-neutral-900 py-20">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="font-mono text-[10px] tracking-[0.4em] text-neutral-500 mb-4">
            02 / STILLS
          </div>
          <h1 className="font-bodoni font-black text-6xl md:text-8xl lg:text-9xl leading-[0.85]">
            Frames in <span className="italic">silver</span>.
          </h1>
          <p className="mt-6 max-w-2xl font-mono text-sm text-neutral-400">
            Editorial, portrait, behind-the-scenes, and product captures.
            Every frame in pure monochrome — high contrast, deep grain.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="font-mono text-xs text-neutral-600 cursor-blink tracking-widest">
              DEVELOPING NEGATIVES
            </div>
          ) : items.length === 0 ? (
            <div className="border border-neutral-900 p-20 text-center" data-testid="stills-empty">
              <div className="font-mono text-[10px] text-neutral-600 tracking-widest mb-3">
                NEGATIVES PENDING
              </div>
              <p className="font-bodoni italic text-3xl text-neutral-500">
                The darkroom is busy.
              </p>
            </div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [&>*]:break-inside-avoid">
              {items.map((m, i) => (
                <motion.button
                  key={m.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.03, 0.4) }}
                  onClick={() => setActiveIdx(i)}
                  data-testid={`still-card-${m.id}`}
                  className="relative block w-full mb-4 group overflow-hidden bg-neutral-950 border border-neutral-900 hover:border-white transition-colors"
                >
                  <img
                    src={fileUrl(m.id)}
                    alt={m.title}
                    className="w-full grayscale group-hover:grayscale-0 transition-all duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="font-mono text-[10px] text-neutral-400 tracking-widest mb-1">
                      STILL.{String(i + 1).padStart(3, "0")}
                    </div>
                    <div className="font-bodoni text-xl">{m.title}</div>
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {activeIdx !== null && items[activeIdx] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex items-center justify-center p-4"
            data-testid="still-lightbox"
          >
            <button data-testid="still-close" onClick={close} className="absolute top-6 right-6 text-white"><X size={28} /></button>
            <button onClick={prev} className="absolute left-6 top-1/2 -translate-y-1/2 text-white hover:text-neutral-400"><ArrowLeft size={28} /></button>
            <button onClick={next} className="absolute right-6 top-1/2 -translate-y-1/2 text-white hover:text-neutral-400"><ArrowRight size={28} /></button>
            <div className="max-w-5xl w-full">
              <img src={fileUrl(items[activeIdx].id)} alt={items[activeIdx].title} className="w-full max-h-[80vh] object-contain" />
              <div className="mt-4 flex justify-between items-baseline">
                <div>
                  <div className="font-mono text-[10px] text-neutral-500 tracking-widest">STILL</div>
                  <div className="font-bodoni text-2xl">{items[activeIdx].title}</div>
                </div>
                <div className="font-mono text-[10px] text-neutral-500">
                  {activeIdx + 1} / {items.length}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
